import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, ExternalLink, FileText } from 'lucide-react';
import PageHero from '../../components/PageHero';
import CTASection from '../../components/CTASection';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { PARTNER_SPECS } from '../../config/partnerSpecs';
import { EDI_INFO } from '../../config/ediInfo';

/**
 * SpecificationDetail - per-partner EDI specification page.
 *
 * Replicates the legacy AngularJS `vendorinfo.html` template in the new design
 * system. Route: /specifications/:id. The `:id` is looked up in PARTNER_SPECS
 * (generated from the legacy specservice.js, 1108 entries).
 *
 * Each partner: { id, name, missing?, customMessage?, fullName?, imgFileName?, ediDocs? }
 * ediDocs rows:
 *   - length === 5 → [transaction, ediDocument, dateUpdated, downloadType, file]
 *   - length !== 5 → [sectionHeader]  (a divider/sub-header row)
 *
 * The ~1.9 GB of spec PDFs are NOT bundled - internal files link to the live
 * legacy site at https://www.iconnect-corp.com/specifications/vendors/<id>/<file>.
 * External `file` values (starting with http) are used as-is.
 */

const SUPPORT_PHONE = '877-275-7417';
const IEXCHANGE_PHONE = '877-327-9334';

/**
 * Resolve a download URL for an ediDocs `file` value.
 * External URLs (http/https) are returned untouched. Internal files live on the
 * live legacy site; each path segment is URL-encoded so spaces (and other
 * special chars) are escaped while directory separators are preserved.
 */
const resolveDocUrl = (id, file) => {
  if (!file) return null;
  if (/^https?:\/\//i.test(file)) return file;
  const encoded = String(file)
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `https://www.iconnect-corp.com/specifications/vendors/${id}/${encoded}`;
};

/** Shared support + iExchangeWeb pitch block (matches the legacy footer copy). */
const AssistanceBlock = () => (
  <div className="mt-10 space-y-6">
    <p className="text-sm text-text-muted">
      If you require further assistance, please contact iConnect support at{' '}
      <span className="font-semibold text-text-secondary">
        <a
          href={`tel:${SUPPORT_PHONE.replace(/-/g, '')}`}
          className="hover:text-accent-primary transition-colors"
        >
          {SUPPORT_PHONE}
        </a>
      </span>{' '}
      or email us at{' '}
      <a
        href="mailto:Support@iConnect-Corp.com"
        className="text-accent-primary font-semibold hover:text-accent-deep transition-colors"
      >
        Support@iConnect-Corp.com
      </a>
    </p>

    {/* iExchangeWeb interim-solution pitch */}
    <div className="card-elevated p-6 lg:p-8 bg-bg-soft border-l-4 border-accent-primary">
      <p className="text-text-secondary leading-relaxed mb-4">
        Do you need to get up and running quickly with this customer, but find
        that it will take 3-6 months to get the setup and mapping done on
        your current EDI system?
      </p>
      <p className="text-text-primary leading-relaxed">
        <span className="font-display font-bold text-gradient-sky">
          iConnect has the solution!
        </span>{' '}
        See how easy and cost-effective it is to use iExchangeWeb as an interim
        solution. To learn more, call us at{' '}
        <a
          href={`tel:${IEXCHANGE_PHONE.replace(/-/g, '')}`}
          className="text-accent-primary font-semibold hover:text-accent-deep transition-colors"
        >
          {IEXCHANGE_PHONE}
        </a>{' '}
        or email{' '}
        <a
          href="mailto:Info@iConnect-Corp.com"
          className="text-accent-primary font-semibold hover:text-accent-deep transition-colors"
        >
          Info@iConnect-Corp.com
        </a>{' '}
        and ask for the Web EDI presentation.
      </p>
    </div>
  </div>
);

/** A single "cell" - shows an inline label on mobile, hides it on desktop. */
const Cell = ({ label, children, className = '' }) => (
  <div className={className}>
    <span className="md:hidden font-mono text-[10px] uppercase tracking-wider text-text-muted mr-2">
      {label}
    </span>
    {children}
  </div>
);

const SpecificationDetail = () => {
  const { id } = useParams();

  // Ids are unique for real partners; the shared "missing" sentinel is never a
  // linkable detail page, so treat it as not-found.
  const partner = useMemo(
    () =>
      id && id !== 'missing'
        ? PARTNER_SPECS.find((p) => p.id === id)
        : undefined,
    [id]
  );

  const displayName = partner ? partner.fullName || partner.name : id;

  useDocumentTitle({
    title: partner
      ? `${partner.name} EDI Specifications`
      : 'Partner not found',
    description: partner
      ? `EDI specifications and transaction documents for ${
          partner.fullName || partner.name
        } - download the guides iConnect uses to connect you to this trading partner.`
      : 'The requested trading partner specification could not be found.',
  });

  // ---- Not found ---------------------------------------------------------
  if (!partner) {
    return (
      <>
        <PageHero
          eyebrow="Specifications"
          title="Partner not"
          titleAccent="found."
          breadcrumbs={[
            { label: 'Connections', to: '/resources/connections' },
            { label: 'Not found' },
          ]}
          subhead="We couldn't find a trading partner specification for that address. It may have moved, or the partner isn't published yet."
        />
        <section className="py-16 lg:py-20 bg-bg-canvas">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="card-elevated p-8 lg:p-10 text-center">
              <p className="text-text-secondary mb-6">
                Browse the full list of trading partners iConnect already
                connects, or ask us to add yours.
              </p>
              <Link
                to="/resources/connections"
                className="btn-primary inline-flex items-center gap-2"
              >
                <ArrowLeft size={16} />
                Back to Connections
              </Link>
            </div>
          </div>
        </section>
        <CTASection />
      </>
    );
  }

  const hasSpecs =
    !partner.missing &&
    Array.isArray(partner.ediDocs) &&
    partner.ediDocs.length > 0;

  const vendorLink = partner.vendorLink ? partner.vendorLink.trim() : null;
  const ediBlocks = EDI_INFO[partner.id] || [];
  const logoSrc = `${process.env.PUBLIC_URL || ''}/vendor-logos/${partner.imgFileName || `${partner.id}.jpg`}`;

  return (
    <>
      <PageHero
        eyebrow="Specifications"
        title={displayName}
        breadcrumbs={[
          { label: 'Connections', to: '/resources/connections' },
          { label: partner.name },
        ]}
        subhead={
          hasSpecs
            ? `EDI transaction specifications for ${displayName}. Download the implementation guides and maps iConnect uses to connect you to this trading partner.`
            : `iConnect connects you to ${displayName}. Detailed EDI specifications for this partner aren't published here yet - reach out and we'll get you the maps you need.`
        }
      />

      <section className="py-16 lg:py-20 bg-bg-canvas">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {/* Back to the directory - always visible, in addition to the
              breadcrumbs in the hero, so users can return to the full list. */}
          <Link
            to="/resources/connections"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-primary hover:text-accent-deep transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to Connections directory
          </Link>

          {/* Optional partner logo - hidden on error (many partners have none). */}
          <img
            src={logoSrc}
            alt={`${partner.name} logo`}
            loading="lazy"
            className="max-h-16 w-auto mb-8 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />

          {/* EDI Information - VAN / ISA (or UNB) identifiers, restored from the
              legacy per-partner vendor.html. Only shown for partners we have it for. */}
          {ediBlocks.length > 0 && (
            <div className="mb-10">
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-text-primary mb-1">
                <span className="text-gradient-sky">EDI</span> Information
              </h2>
              <p className="text-text-secondary mb-5">
                {displayName}'s EDI information follows.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {ediBlocks.map((block, bi) => (
                  <div key={bi} className="card-elevated p-6">
                    {block.head && (
                      <div className="font-display font-bold text-text-primary mb-3">
                        {block.head}
                      </div>
                    )}
                    <dl className="space-y-2">
                      {block.pairs.map((pair, pi) => (
                        <div key={pi} className="flex items-baseline gap-3">
                          <dt className="font-mono text-xs uppercase tracking-wider text-text-muted w-28 flex-shrink-0">
                            {pair.label}
                          </dt>
                          <dd className="text-text-primary font-semibold break-words">
                            {pair.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasSpecs ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <p className="text-text-secondary max-w-2xl leading-relaxed">
                  Below are the specifications for{' '}
                  <span className="font-semibold text-text-primary">
                    {displayName}
                  </span>
                  's EDI transactions. Please check back for changes and
                  additions.
                </p>
                {vendorLink && (
                  <a
                    href={vendorLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary inline-flex items-center gap-2 whitespace-nowrap"
                  >
                    Visit trading partner's website
                    <ExternalLink size={15} />
                  </a>
                )}
              </div>

              {/* Specifications table */}
              <div className="card-elevated overflow-hidden p-0">
                {/* Header - desktop only */}
                <div className="hidden md:grid grid-cols-[2fr_2fr_1fr_1fr] gap-4 px-6 py-4 bg-bg-soft border-b border-border-subtle">
                  <span className="font-mono text-xs uppercase tracking-wider text-text-secondary">
                    Transaction
                  </span>
                  <span className="font-mono text-xs uppercase tracking-wider text-text-secondary">
                    EDI Document
                  </span>
                  <span className="font-mono text-xs uppercase tracking-wider text-text-secondary">
                    Date Updated
                  </span>
                  <span className="font-mono text-xs uppercase tracking-wider text-text-secondary">
                    Downloads
                  </span>
                </div>

                <div className="divide-y divide-border-subtle">
                  {partner.ediDocs.map((row, idx) => {
                    // Section sub-header / divider row.
                    if (row.length !== 5) {
                      return (
                        <div
                          key={`section-${idx}`}
                          className="px-6 py-3 bg-bg-soft"
                        >
                          <span className="font-mono text-xs uppercase tracking-wider font-semibold text-accent-deep">
                            {row[0]}
                          </span>
                        </div>
                      );
                    }

                    // Data row: [transaction, ediDocument, dateUpdated, downloadType, file]
                    const [transaction, ediDocument, dateUpdated, downloadType, file] =
                      row;
                    const url = resolveDocUrl(partner.id, file);

                    return (
                      <motion.div
                        key={`row-${idx}`}
                        initial={{ opacity: 0.6, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                        className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_1fr] gap-1.5 md:gap-4 px-6 py-4 hover:bg-bg-soft/60 transition-colors"
                      >
                        <Cell
                          label="Transaction"
                          className="font-semibold text-text-primary"
                        >
                          {transaction}
                        </Cell>
                        <Cell label="EDI Document" className="text-text-secondary">
                          {ediDocument}
                        </Cell>
                        <Cell
                          label="Date Updated"
                          className="text-text-muted text-sm md:text-base"
                        >
                          {dateUpdated}
                        </Cell>
                        <Cell label="Downloads">
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-accent-primary font-semibold hover:text-accent-deep transition-colors"
                            >
                              <FileText size={14} />
                              {downloadType}
                            </a>
                          ) : (
                            <span className="text-text-muted">{downloadType}</span>
                          )}
                        </Cell>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            /* No specs published yet */
            <div className="card-elevated p-8 lg:p-10">
              <p className="eyebrow mb-3">Specifications</p>
              <h2 className="font-display font-bold text-2xl text-text-primary mb-3">
                Specs aren't published here yet
              </h2>
              <p className="text-text-secondary leading-relaxed mb-6 max-w-2xl">
                iConnect connects trading partners irrespective of data format,
                technology, or system language. We haven't published the detailed
                EDI specifications for{' '}
                <span className="font-semibold text-text-primary">
                  {displayName}
                </span>{' '}
                on this page yet - but we can get them to you. Reach out and we'll
                share the maps and implementation guides you need.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
              >
                Request these specifications
                <ArrowUpRight size={16} />
              </Link>
            </div>
          )}

          {/* Support + iExchangeWeb pitch - always rendered for a found partner. */}
          <AssistanceBlock />
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default SpecificationDetail;
